import React, { useState } from 'react';
import {
    Container, Box, TextField, Button, Typography, Paper, Stack, MenuItem
} from '@mui/material';
import { usePersistentNavigate } from '../utils/navigation';
import { useAuthContext } from '../context/AuthContext';
import { registerReviewer } from '../queries/registerreviewer';
import { Reviewer, CancerType } from '../schema';

const CANCER_TYPES = [
    { value: 'BREAST', label: 'Breast' },
    { value: 'THORACIC', label: 'Thoracic' },
    { value: 'GI', label: 'GI' },
    { value: 'GU', label: 'GU' },
    { value: 'GYNO', label: 'Gyno' },
    { value: 'HEAD_NECK', label: 'Head & Neck' },
    { value: 'NEURO', label: 'Neuro' },
    { value: 'SKIN', label: 'Skin' },
    { value: 'HEME', label: 'Heme' },
    { value: 'OTHER', label: 'Other' },
];

const RegisterReviewer: React.FC = () => {
    const navigate = usePersistentNavigate();
    const { accessToken, setAccessToken, logout } = useAuthContext();

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [medicalSchool, setMedicalSchool] = useState('');
    const [degree, setDegree] = useState('');
    const [hospital, setHospital] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [specialty, setSpecialty] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const reviewer: Reviewer = {
            id: '', // Not needed for creation
            firstName,
            lastName,
            emailAddress: email,
            phoneNumber: phone,
            medicalSchool: medicalSchool || undefined,
            degree: degree || undefined,
            hospitalOrPractice: hospital,
            city,
            state,
            specialty,
            mfaEnabled: true,
            notificationChannels: { fields: {} },
            notificationSettings: { fields: {} },
            isActive: true,
            createdAt: undefined,
            updatedAt: undefined,
        };

        try {
            await registerReviewer({ reviewer, password }, accessToken!, setAccessToken, logout);
            navigate({ pathname: '/registration-result', search: 'success=true&type=reviewer' });
        } catch (err) {
            setError((err as Error).message);
            navigate({ pathname: '/registration-result', search: 'success=false&type=reviewer' });
        }
    };

    return (
        <Container maxWidth="sm">
            <Paper sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Register New Reviewer
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} fullWidth required />
                        <TextField label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} fullWidth required />
                        <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} fullWidth required />
                        <TextField label="Phone Number" value={phone} onChange={e => setPhone(e.target.value)} fullWidth required />

                        <TextField label="Medical School" value={medicalSchool} onChange={e => setMedicalSchool(e.target.value)} fullWidth />
                        <TextField
                            select
                            label="Degree"
                            value={degree}
                            onChange={e => setDegree(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">Select Degree</MenuItem>
                            <MenuItem value="MD">MD</MenuItem>
                            <MenuItem value="DO">DO</MenuItem>
                        </TextField>

                        <TextField label="Hospital or Practice" value={hospital} onChange={e => setHospital(e.target.value)} fullWidth required />
                        <TextField label="City" value={city} onChange={e => setCity(e.target.value)} fullWidth required />
                        <TextField label="State" value={state} onChange={e => setState(e.target.value)} fullWidth required />

                        <TextField
                            select
                            label="Specialty"
                            value={specialty}
                            onChange={e => setSpecialty(e.target.value)}
                            fullWidth
                            required
                        >
                            <MenuItem value="">Select Specialty</MenuItem>
                            {CANCER_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} fullWidth required />

                        {error && <Typography color="error">{error}</Typography>}
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained">Register Reviewer</Button>
                            <Button variant="outlined" onClick={() => navigate('/admin')}>Back to Admin Portal</Button>
                        </Stack>
                    </Stack>
                </Box>
            </Paper>
        </Container>
    );
};

export default RegisterReviewer;
